import * as THREE from 'three';
import * as CANNON from 'cannon-es';
import { FpsTouchControls, type AimAssistTarget } from './FpsTouchControls.js';
import { getWeaponRecoilForShot, type WeaponKind } from './WeaponSystem.js';
import type { FootstepSurface } from './WorldBuilder.js';
import { computeFallDamage as computeFallDamageRule } from './matchRules.js';

const EYE = 1.62;
const HALF_H = 0.9;
const VAULT_MIN_RISE = 0.8;
const VAULT_MAX_RISE = 2.2;
const VAULT_COOLDOWN = 0.5;
const VAULT_DURATION = 0.36;
const VAULT_PROBE_FWD = 0.52;
const VAULT_EDGE_PROBE_FWD = 0.38;
const MANTLE_FAIL_COOLDOWN = 0.55;
const SLIDE_CROUCH_MOMENTUM = 0.8;
/** Arena ocean inner edge (WorldBuilder ring starts at 94). */
const WATER_RING_INNER = 88;
const SWIM_BODY_Y = 0.42;
const SWIM_SPEED = 3.6;
const SWIM_BOB_AMP = 0.09;
const SWIM_BOB_FREQ = 2.4;
const SWIM_UP_SPEED = 5.8;
const SWIM_SPRINT_MULT = 1.55;
const SWIM_SPRINT_STAMINA_DRAIN = 28;
const GLIDER_FALL_GRACE_SEC = 2;
const GAMEPAD_LOOK_DEADZONE = 0.12;
const GAMEPAD_MOVE_DEADZONE = 0.16;
const GAMEPAD_LOOK_SENS = 2.85;
const FALL_DAMAGE_SAFE_VY = -6;
const FALL_DAMAGE_PER_VY = 3.5;
const FALL_DAMAGE_MAX = 95;

export class PlayerController {
  readonly yawObject = new THREE.Object3D();
  readonly pitchObject = new THREE.Object3D();
  readonly body: CANNON.Body;
  private keys = new Set<string>();
  private pitchAngle = 0;
  private canJump = false;
  private coyoteLeft = 0;
  private pointerLocked = false;
  private cameraRoll = 0;
  private juiceCameraRoll = 0;
  private lookSensitivity = 1;
  private adsSensitivity = 0.7;
  private adsActive = false;
  private prevGrounded = true;
  private viewKickY = 0;
  private recoilPitch = 0;
  private recoilYaw = 0;
  private recoilPatternIndex = 0;
  private recoilResetTimer = 0;
  private recoilSpringSpeed = 14;
  private shakeX = 0;
  private shakeY = 0;
  private chestOpenPitch = 0;
  private mobilityPitchNudge = 0;
  private gliderFallGraceTimer = 0;
  private prevGliderWanted = false;
  private swimSprinting = false;
  private footstepRaycastRoot: THREE.Object3D | null = null;
  private readonly footstepRaycaster = new THREE.Raycaster();
  private cachedFootstepSurface: FootstepSurface = 'grass';
  private landingPulse = false;
  private introFrozen = false;
  private movementLocked = false;
  private inputLocked = false;
  private sliding = false;
  private sprintHold = 0;
  private stamina = 100.0;
  private slideTime = 0;
  private gliderActive = false;
  private jumpBufferLeft = 0;
  private knocked = false;
  private carryingSquadmate = false;
  private vaultCooldown = 0;
  private vaulting = false;
  private vaultT = 0;
  private vaultStartY = 0;
  private vaultTargetY = 0;
  private vaultFwdX = 0;
  private vaultFwdZ = 0;
  private autoRun = false;
  private swimming = false;
  private prevSwimming = false;
  private swimEntryPulse = false;
  private swimPhase = 0;
  private sprintFovPunch = 0;
  private wasSprintingForFov = false;
  private wasTacticalSprintingForFov = false;
  private reducedMotion = false;
  private wasSliding = false;
  private mantleFailCooldown = 0;
  private mantleFailPromptPending = false;
  private gamepadAimTargets: AimAssistTarget[] = [];
  private gamepadAimAssistEnabled = true;
  private gamepadMoveX = 0;
  private gamepadMoveY = 0;

  private baseFov = 90;
  private currentFov = 90;

  constructor(
    private world: CANNON.World,
    public camera: THREE.PerspectiveCamera,
    start: THREE.Vector3,
    playerMat: CANNON.Material,
  ) {
    this.baseFov = camera.fov;
    this.currentFov = this.baseFov;
    this.yawObject.add(this.pitchObject);
    this.pitchObject.position.set(0, EYE, 0);
    this.pitchObject.add(this.camera);

    const shape = new CANNON.Box(new CANNON.Vec3(0.35, HALF_H, 0.35));
    this.body = new CANNON.Body({
      mass: 78,
      fixedRotation: true,
      linearDamping: 0.22,
      material: playerMat,
    });
    this.body.addShape(shape);
    this.body.position.set(start.x, HALF_H + 0.02, start.z);
    this.body.sleepSpeedLimit = 0.1;
    world.addBody(this.body);

    window.addEventListener('keydown', this.onKey);
    window.addEventListener('keyup', this.onKeyUp);
    document.addEventListener('pointerlockchange', this.onLockChange);
    document.addEventListener('mousemove', this.onMouse);
  }

  dispose(): void {
    window.removeEventListener('keydown', this.onKey);
    window.removeEventListener('keyup', this.onKeyUp);
    document.removeEventListener('pointerlockchange', this.onLockChange);
    document.removeEventListener('mousemove', this.onMouse);
    this.world.removeBody(this.body);
  }

  requestPointerLock(el: HTMLElement): void {
    try {
      const ret = el.requestPointerLock() as void | Promise<void>;
      if (ret && typeof (ret as Promise<void>).catch === 'function') {
        (ret as Promise<void>).catch(() => {
          /* headless / non-user-gesture — ignore */
        });
      }
    } catch {
      /* WrongDocumentError or unsupported in automation */
    }
  }

  private onLockChange = (): void => {
    this.pointerLocked = document.pointerLockElement !== null;
  };

  private getLookSensMul(): number {
    return this.lookSensitivity * (this.adsActive ? this.adsSensitivity : 1);
  }

  private onMouse = (e: MouseEvent): void => {
    if (!this.pointerLocked || this.inputLocked) return;
    const sens = 0.0022 * this.getLookSensMul();
    this.yawObject.rotation.y -= e.movementX * sens;
    this.pitchAngle -= e.movementY * sens;
    this.pitchAngle = Math.max(-Math.PI / 2 + 0.05, Math.min(Math.PI / 2 - 0.05, this.pitchAngle));
  };

  /** Single-frame jump pulse (touch). */
  injectJumpPulse(): void {
    this.keys.add('Space');
    window.setTimeout(() => this.keys.delete('Space'), 120);
  }

  /** Inject virtual key state (touch controls). */
  injectKey(code: string, down: boolean): void {
    if (down) this.keys.add(code);
    else this.keys.delete(code);
  }

  setAutoRun(on: boolean): void {
    this.autoRun = on;
  }

  toggleAutoRun(): boolean {
    this.autoRun = !this.autoRun;
    return this.autoRun;
  }

  isAutoRun(): boolean {
    return this.autoRun;
  }

  /** Inject look delta from touch drag. */
  injectLook(dx: number, dy: number): void {
    const sens = 0.0048 * this.getLookSensMul();
    this.yawObject.rotation.y -= dx * sens;
    this.pitchAngle -= dy * sens;
    this.pitchAngle = Math.max(-Math.PI / 2 + 0.05, Math.min(Math.PI / 2 - 0.05, this.pitchAngle));
  }

  /** World targets for controller aim assist magnet (updated each frame). */
  setGamepadAimTargets(targets: AimAssistTarget[]): void {
    this.gamepadAimTargets = targets;
  }

  setGamepadAimAssistEnabled(on: boolean): void {
    this.gamepadAimAssistEnabled = on;
  }

  /** Left-stick movement axes after deadzone (for gamepad locomotion). */
  getGamepadMoveAxes(): { x: number; y: number } {
    return { x: this.gamepadMoveX, y: this.gamepadMoveY };
  }

  /** Touch aim assist — nudge yaw/pitch toward a world target. */
  applyAimNudge(yawDelta: number, pitchDelta: number): void {
    this.yawObject.rotation.y += yawDelta;
    this.pitchAngle += pitchDelta;
    this.pitchAngle = Math.max(-Math.PI / 2 + 0.05, Math.min(Math.PI / 2 - 0.05, this.pitchAngle));
  }

  private onKey = (e: KeyboardEvent): void => {
    this.keys.add(e.code);
    if (e.code === 'Space') {
      this.jumpBufferLeft = 0.14;
      e.preventDefault();
    }
  };

  private onKeyUp = (e: KeyboardEvent): void => {
    this.keys.delete(e.code);
  };

  isLocked(): boolean {
    return this.pointerLocked;
  }

  isKeyHeld(code: string): boolean {
    return this.keys.has(code);
  }

  setIntroFrozen(on: boolean): void {
    this.introFrozen = on;
    if (on) {
      this.body.velocity.set(0, 0, 0);
    }
  }

  isIntroFrozen(): boolean {
    return this.introFrozen;
  }

  setMovementLocked(on: boolean): void {
    this.movementLocked = on;
    if (on) {
      this.body.velocity.set(0, 0, 0);
    }
  }

  setInputLocked(on: boolean): void {
    this.inputLocked = on;
  }

  isInputLocked(): boolean {
    return this.inputLocked;
  }

  setKnocked(on: boolean): void {
    this.knocked = on;
    if (on) {
      this.sliding = false;
      this.gliderActive = false;
      this.sprintHold = 0;
    }
  }

  isKnocked(): boolean {
    return this.knocked;
  }

  setCarrying(on: boolean): void {
    this.carryingSquadmate = on;
    if (on) {
      this.sliding = false;
      this.sprintHold = 0;
    }
  }

  isCarryingSquadmate(): boolean {
    return this.carryingSquadmate;
  }

  /** Seconds player has held sprint while moving (for tactical sprint HUD). */
  getSprintHold(): number {
    return this.sprintHold;
  }

  isSliding(): boolean {
    return this.sliding;
  }

  getStamina(): number {
    return this.stamina;
  }

  /** True while sprinting in water (Shift + movement). */
  isSwimSprinting(): boolean {
    return this.swimSprinting;
  }

  /** Seconds of fall-damage immunity remaining after glider deploy. */
  getGliderFallGrace(): number {
    return this.gliderFallGraceTimer;
  }

  /** Fall damage from landing impact velocity; 0 while glider grace is active. */
  computeFallDamage(impactVy: number): number {
    return computeFallDamageRule(
      impactVy,
      this.gliderFallGraceTimer > 0,
      FALL_DAMAGE_SAFE_VY,
      FALL_DAMAGE_PER_VY,
      FALL_DAMAGE_MAX,
    );
  }

  /** External mobility camera pitch offset (zipline mount/dismount). */
  setMobilityPitchNudge(nudge: number): void {
    this.mobilityPitchNudge = nudge;
  }

  isGrounded(): boolean {
    return this.prevGrounded;
  }

  /** True while tactical sprint speed boost is active. */
  getTacticalSprintActive(): boolean {
    return (
      !this.knocked &&
      this.sprintHold > 0.58 &&
      (this.keys.has('ShiftLeft') || this.keys.has('ShiftRight'))
    );
  }

  /** Extra camera roll from movement juice (radians, additive to strafe roll). */
  setCameraRoll(roll: number): void {
    this.juiceCameraRoll = roll;
  }

  setLookSensitivity(multiplier: number): void {
    this.lookSensitivity = Math.max(0.25, Math.min(4, multiplier));
  }

  setAdsSensitivity(multiplier: number): void {
    this.adsSensitivity = Math.max(0.25, Math.min(1.5, multiplier));
  }

  setAdsActive(on: boolean): void {
    this.adsActive = on;
  }

  /** Space-held sky glide (umbrella-style slow fall). */
  isGliderActive(): boolean {
    return this.gliderActive;
  }

  /** True while in the outer ocean ring (hypot(x,z) > 88). */
  isSwimming(): boolean {
    return this.swimming;
  }

  /** True once when the player enters the water zone (swim entry splash). */
  consumeSwimEntryPulse(): boolean {
    const pulse = this.swimEntryPulse;
    this.swimEntryPulse = false;
    return pulse;
  }

  private isInWaterZone(): boolean {
    const x = this.body.position.x;
    const z = this.body.position.z;
    return Math.hypot(x, z) > WATER_RING_INNER;
  }

  /** True while mantle/vault animation is driving the body. */
  isVaulting(): boolean {
    return this.vaulting;
  }

  /** Lower camera / eye when crouching (Key C). */
  getCrouchEyeDrop(): number {
    return this.keys.has('KeyC') ? 0.44 : 0;
  }

  /** Pickaxe swing duration per combo step (seconds). */
  getPickaxeSwingDuration(comboHit: 1 | 2 | 3): number {
    switch (comboHit) {
      case 1:
        return 0.24;
      case 2:
        return 0.22;
      case 3:
        return 0.3;
      default: {
        const _exhaustive: never = comboHit;
        return _exhaustive;
      }
    }
  }

  /** Forward lunge on pickaxe combo finisher. */
  applyPickaxeFinisherLunge(dir: THREE.Vector3): void {
    if (this.knocked || this.movementLocked || this.introFrozen) return;
    const lunge = 5.5;
    this.body.velocity.x += dir.x * lunge;
    this.body.velocity.z += dir.z * lunge;
  }

  /** Brief upward and lateral camera kick on shots. */
  applyShootRecoil(amount = 0.026): void {
    // Add upward kick
    this.recoilPitch = Math.min(0.18, this.recoilPitch + amount);
    // Add slight random horizontal drift
    const yawKick = (Math.random() - 0.5) * amount * 0.4;
    this.yawObject.rotation.y += yawKick;
  }

  /** Per-weapon pattern-based recoil. Call with current weapon kind on each shot. */
  fireWeaponRecoil(kind: string): void {
    const idx = this.recoilPatternIndex;
    this.recoilPatternIndex++;
    this.recoilResetTimer = 0.35;

    const weaponKind = (['ar', 'shotgun', 'sniper', 'smg', 'pistol'] as const).includes(kind as WeaponKind)
      ? (kind as WeaponKind)
      : 'ar';
    const step = getWeaponRecoilForShot(weaponKind, idx);

    const applyStep = (pitch: number, yaw: number, springSpeed: number): void => {
      this.recoilPitch = Math.max(-0.18, this.recoilPitch + pitch);
      this.recoilYaw += yaw;
      this.recoilSpringSpeed = springSpeed;
    };

    if (weaponKind === 'sniper') {
      setTimeout(() => applyStep(step.pitch, step.yaw, step.springSpeed), 200);
      return;
    }
    applyStep(step.pitch, step.yaw, step.springSpeed);
  }

  setReducedMotion(on: boolean): void {
    this.reducedMotion = on;
    if (on) {
      this.shakeX = 0;
      this.shakeY = 0;
      this.camera.position.set(0, 0, 0);
    }
  }

  /** Small random camera shake (e.g. shotgun). */
  applyCameraShake(intensity = 0.012): void {
    if (this.reducedMotion) return;
    this.shakeX += (Math.random() - 0.5) * intensity * 2.2;
    this.shakeY += (Math.random() - 0.5) * intensity * 2.2;
  }

  /** Brief look-down nudge when a chest opens. */
  applyChestOpenNudge(tier: 'normal' | 'rare' = 'normal'): void {
    if (this.reducedMotion) return;
    this.chestOpenPitch = tier === 'rare' ? -0.042 : -0.03;
    this.shakeY += tier === 'rare' ? 0.007 : 0.004;
  }

  /** Camera punch when the glider folds and feet touch ground. */
  applyGliderLandingPunch(): void {
    if (this.reducedMotion) return;
    this.viewKickY = Math.max(this.viewKickY, 0.072);
    this.shakeY += 0.016;
    this.shakeX += (Math.random() - 0.5) * 0.008;
  }

  setFootstepRaycastRoot(root: THREE.Object3D): void {
    this.footstepRaycastRoot = root;
  }

  getFootstepSurface(): FootstepSurface {
    return this.cachedFootstepSurface;
  }

  private updateFootstepSurface(): void {
    if (!this.footstepRaycastRoot) return;
    const ox = this.body.position.x;
    const oy = this.body.position.y - HALF_H + 0.05;
    const oz = this.body.position.z;
    // Camera required so THREE.Sprite raycasts do not throw (matrixWorld of null).
    this.footstepRaycaster.camera = this.camera;
    this.footstepRaycaster.set(
      new THREE.Vector3(ox, oy + 0.45, oz),
      new THREE.Vector3(0, -1, 0),
    );
    this.footstepRaycaster.far = 1.25;
    let hits: THREE.Intersection[];
    try {
      hits = this.footstepRaycaster.intersectObject(this.footstepRaycastRoot, true);
    } catch {
      this.cachedFootstepSurface = 'grass';
      return;
    }
    for (const hit of hits) {
      // Skip sprites/points — only mesh footstep tags matter
      if (!(hit.object as THREE.Mesh).isMesh) continue;
      const surface = hit.object.userData.footstepSurface as FootstepSurface | undefined;
      if (surface) {
        this.cachedFootstepSurface = surface;
        return;
      }
    }
    this.cachedFootstepSurface = 'grass';
  }

  /** True once after a hard landing; cleared on read. */
  consumeLandingPulse(): boolean {
    const pulse = this.landingPulse;
    this.landingPulse = false;
    return pulse;
  }

  /** True once when mantle fails; cleared on read. */
  consumeMantleFailPrompt(): boolean {
    const pending = this.mantleFailPromptPending;
    this.mantleFailPromptPending = false;
    return pending;
  }

  /** Positive = camera dips (landing). */
  getViewKickY(): number {
    return this.viewKickY;
  }

  /** World position slightly behind the player for trail VFX. */
  getGliderTrailPosition(out: THREE.Vector3): void {
    const yaw = this.yawObject.rotation.y;
    out.set(
      this.body.position.x + Math.sin(yaw) * 0.55,
      this.body.position.y - 0.35,
      this.body.position.z + Math.cos(yaw) * 0.55,
    );
  }

  /** Feet-level position for landing burst VFX. */
  getLandingFxPosition(out: THREE.Vector3): void {
    out.set(this.body.position.x, this.body.position.y - HALF_H + 0.08, this.body.position.z);
  }

  private decayCameraShake(dt: number): void {
    if (this.reducedMotion) {
      this.shakeX = 0;
      this.shakeY = 0;
      this.camera.position.set(0, 0, 0);
      return;
    }
    this.shakeX *= Math.exp(-18 * dt);
    this.shakeY *= Math.exp(-18 * dt);
    this.camera.position.set(this.shakeX, this.shakeY, 0);
  }

  private decayChestOpenPitch(dt: number): void {
    this.chestOpenPitch *= Math.exp(-16 * dt);
  }

  private getPitchWithJuice(): number {
    return this.pitchAngle + this.recoilPitch + this.chestOpenPitch + this.mobilityPitchNudge;
  }

  private decayRecoil(dt: number): void {
    const springDecay = Math.exp(-this.recoilSpringSpeed * dt);
    this.recoilPitch *= springDecay;
    this.recoilYaw   *= springDecay;
    this.yawObject.rotation.y += this.recoilYaw * dt * this.recoilSpringSpeed * 0.02;
    if (this.recoilResetTimer > 0) {
      this.recoilResetTimer -= dt;
      if (this.recoilResetTimer <= 0) {
        this.recoilPatternIndex = 0;
      }
    }
  }

  private detectVaultLedge(
    feetY: number,
    fwdX: number,
    fwdZ: number,
    chestBlocked: boolean,
  ): { topBodyY: number } | null {
    const px = this.body.position.x;
    const pz = this.body.position.z;
    const probeX = px + fwdX * VAULT_PROBE_FWD;
    const probeZ = pz + fwdZ * VAULT_PROBE_FWD;
    const edgeX = px + fwdX * VAULT_EDGE_PROBE_FWD;
    const edgeZ = pz + fwdZ * VAULT_EDGE_PROBE_FWD;

    const downFrom = new CANNON.Vec3(probeX, feetY + VAULT_MAX_RISE + 0.45, probeZ);
    const downTo = new CANNON.Vec3(probeX, feetY - 0.25, probeZ);
    const downRes = new CANNON.RaycastResult();
    if (!this.world.raycastClosest(downFrom, downTo, { skipBackfaces: true }, downRes) || !downRes.hasHit) {
      return null;
    }

    const rise = downRes.hitPointWorld.y - feetY;
    if (rise < VAULT_MIN_RISE || rise > VAULT_MAX_RISE) return null;

    const lipFrom = new CANNON.Vec3(px, feetY + 0.12, pz);
    const lipTo = new CANNON.Vec3(probeX, feetY + 0.12, probeZ);
    const lipRes = new CANNON.RaycastResult();
    const lipHit = this.world.raycastClosest(lipFrom, lipTo, { skipBackfaces: true }, lipRes);
    const lipDrop =
      lipHit && lipRes.hasHit ? feetY + 0.12 - lipRes.hitPointWorld.y : VAULT_MAX_RISE + 1;
    const atLedge = lipDrop >= VAULT_MIN_RISE && lipDrop <= VAULT_MAX_RISE;

    const kneeFrom = new CANNON.Vec3(px, feetY + 0.62, pz);
    const kneeTo = new CANNON.Vec3(edgeX, feetY + 0.62, edgeZ);
    const kneeRes = new CANNON.RaycastResult();
    const kneeHit = this.world.raycastClosest(kneeFrom, kneeTo, { skipBackfaces: true }, kneeRes);
    const kneeDrop =
      kneeHit && kneeRes.hasHit ? feetY + 0.62 - kneeRes.hitPointWorld.y : VAULT_MAX_RISE + 1;
    const edgeLip = kneeDrop >= VAULT_MIN_RISE * 0.92 && kneeDrop <= VAULT_MAX_RISE + 0.15;

    const edgeDownFrom = new CANNON.Vec3(edgeX, feetY + VAULT_MAX_RISE + 0.35, edgeZ);
    const edgeDownTo = new CANNON.Vec3(edgeX, feetY - 0.2, edgeZ);
    const edgeDownRes = new CANNON.RaycastResult();
    const edgeSurface =
      this.world.raycastClosest(edgeDownFrom, edgeDownTo, { skipBackfaces: true }, edgeDownRes) &&
      edgeDownRes.hasHit;
    const edgeRise = edgeSurface ? edgeDownRes.hitPointWorld.y - feetY : 0;
    const edgeClear = edgeSurface && edgeRise >= VAULT_MIN_RISE * 0.85 && edgeRise <= VAULT_MAX_RISE + 0.2;

    if (!chestBlocked && !atLedge && !(edgeLip && edgeClear)) return null;

    const headFrom = new CANNON.Vec3(probeX, downRes.hitPointWorld.y + 0.1, probeZ);
    const headTo = new CANNON.Vec3(probeX, downRes.hitPointWorld.y + HALF_H * 2 + 0.2, probeZ);
    const headRes = new CANNON.RaycastResult();
    if (
      this.world.raycastClosest(headFrom, headTo, { skipBackfaces: true }, headRes) &&
      headRes.hasHit &&
      headRes.distance < HALF_H * 2 - 0.12
    ) {
      return null;
    }

    const forwardFrom = new CANNON.Vec3(probeX, downRes.hitPointWorld.y + 0.35, probeZ);
    const forwardTo = new CANNON.Vec3(
      probeX + fwdX * 0.42,
      downRes.hitPointWorld.y + 0.35,
      probeZ + fwdZ * 0.42,
    );
    const forwardRes = new CANNON.RaycastResult();
    if (
      this.world.raycastClosest(forwardFrom, forwardTo, { skipBackfaces: true }, forwardRes) &&
      forwardRes.hasHit &&
      forwardRes.distance < 0.28
    ) {
      return null;
    }

    return { topBodyY: downRes.hitPointWorld.y + HALF_H + 0.02 };
  }

  private triggerMantleFailFeedback(): void {
    if (this.mantleFailCooldown > 0) return;
    this.mantleFailCooldown = MANTLE_FAIL_COOLDOWN;
    this.mantleFailPromptPending = true;
    this.applyCameraShake(0.028);
    this.viewKickY = Math.min(0.09, this.viewKickY + 0.045);
  }

  private startVault(topBodyY: number, fwdX: number, fwdZ: number): void {
    this.vaulting = true;
    this.vaultT = 0;
    this.vaultStartY = this.body.position.y;
    this.vaultTargetY = topBodyY;
    this.vaultFwdX = fwdX;
    this.vaultFwdZ = fwdZ;
    this.vaultCooldown = VAULT_COOLDOWN;
    this.jumpBufferLeft = 0;
    this.coyoteLeft = 0;
    this.gliderActive = false;
  }

  setBaseFov(fov: number): void {
    this.baseFov = fov;
  }

  /** Radial deadzone + quadratic response curve for analog sticks. */
  private applyStickCurve(value: number, deadzone: number): number {
    const abs = Math.abs(value);
    if (abs < deadzone) return 0;
    const sign = Math.sign(value);
    const scaled = (abs - deadzone) / (1 - deadzone);
    return sign * scaled * scaled;
  }

  private getActiveGamepad(): Gamepad | null {
    if (typeof navigator === 'undefined' || !navigator.getGamepads) return null;
    const pads = navigator.getGamepads();
    for (const pad of pads) {
      if (pad?.connected) return pad;
    }
    return null;
  }

  private pollGamepad(dt: number): void {
    const pad = this.getActiveGamepad();
    if (!pad || this.inputLocked) {
      this.gamepadMoveX = 0;
      this.gamepadMoveY = 0;
      return;
    }

    const lookX = this.applyStickCurve(pad.axes[2] ?? 0, GAMEPAD_LOOK_DEADZONE);
    const lookY = this.applyStickCurve(pad.axes[3] ?? 0, GAMEPAD_LOOK_DEADZONE);
    if (lookX !== 0 || lookY !== 0) {
      const sens = 0.042 * this.getLookSensMul() * GAMEPAD_LOOK_SENS * dt;
      this.yawObject.rotation.y -= lookX * sens;
      this.pitchAngle -= lookY * sens;
      this.pitchAngle = Math.max(-Math.PI / 2 + 0.05, Math.min(Math.PI / 2 - 0.05, this.pitchAngle));
    }

    this.gamepadMoveX = this.applyStickCurve(pad.axes[0] ?? 0, GAMEPAD_MOVE_DEADZONE);
    this.gamepadMoveY = this.applyStickCurve(pad.axes[1] ?? 0, GAMEPAD_MOVE_DEADZONE);

    const fireHeld = (pad.buttons[7]?.pressed ?? false) || (pad.buttons[5]?.pressed ?? false);
    const adsHeld = pad.buttons[6]?.pressed ?? false;
    const stickMag = Math.hypot(lookX, lookY);
    if (
      this.gamepadAimAssistEnabled &&
      fireHeld &&
      !adsHeld &&
      stickMag < 0.28 &&
      this.gamepadAimTargets.length > 0
    ) {
      const eyeY = this.body.position.y + EYE - this.getCrouchEyeDrop();
      const target = FpsTouchControls.findAimAssistTarget(
        this.camera,
        this.body.position.x,
        eyeY,
        this.body.position.z,
        this.gamepadAimTargets,
        14,
        42,
      );
      if (target) {
        const nudge = FpsTouchControls.applyAimNudge(
          this.camera,
          this.body.position.x,
          eyeY,
          this.body.position.z,
          target,
          FpsTouchControls.getAimAssistStrength() * 0.85,
        );
        this.applyAimNudge(nudge.yawDelta, nudge.pitchDelta);
      }
    }
  }

  update(dt: number): void {
    const vyIn = this.body.velocity.y;
    const feetY = this.body.position.y - HALF_H;
    this.yawObject.position.set(this.body.position.x, feetY, this.body.position.z);
    this.jumpBufferLeft = Math.max(0, this.jumpBufferLeft - dt);
    this.vaultCooldown = Math.max(0, this.vaultCooldown - dt);
    this.mantleFailCooldown = Math.max(0, this.mantleFailCooldown - dt);

    if (this.introFrozen || this.movementLocked) {
      this.gliderActive = false;
      this.swimming = false;
      this.wasSprintingForFov = false;
      this.wasTacticalSprintingForFov = false;
      this.body.velocity.set(0, 0, 0);
      this.decayRecoil(dt);
      this.decayChestOpenPitch(dt);
      this.pitchObject.rotation.x = this.getPitchWithJuice();
      this.decayCameraShake(dt);
      return;
    }

    const wasSwimming = this.swimming;
    this.swimming = !this.knocked && this.isInWaterZone();
    if (this.swimming && !wasSwimming) {
      this.swimEntryPulse = true;
    }
    this.prevSwimming = this.swimming;

    const from = new CANNON.Vec3(this.body.position.x, this.body.position.y - HALF_H + 0.08, this.body.position.z);
    const to = new CANNON.Vec3(this.body.position.x, this.body.position.y - HALF_H - 0.45, this.body.position.z);
    const res = new CANNON.RaycastResult();
    const hit = this.world.raycastClosest(from, to, { skipBackfaces: true }, res);
    const grounded = Boolean(hit && res.hasHit && res.distance < 0.42);
    this.canJump = grounded;

    if (grounded) {
      this.coyoteLeft = 0.11;
    } else {
      this.coyoteLeft = Math.max(0, this.coyoteLeft - dt);
    }

    const crouching = this.knocked || (!this.swimming && this.keys.has('KeyC'));
    this.pollGamepad(dt);

    const forward = new THREE.Vector3(0, 0, -1).applyAxisAngle(new THREE.Vector3(0, 1, 0), this.yawObject.rotation.y);
    const right = new THREE.Vector3(1, 0, 0).applyAxisAngle(new THREE.Vector3(0, 1, 0), this.yawObject.rotation.y);

    if (this.swimming) {
      this.gliderActive = false;
      this.sliding = false;
      this.vaulting = false;
      this.swimPhase += dt * SWIM_BOB_FREQ;
      const bob = Math.sin(this.swimPhase) * SWIM_BOB_AMP;
      const targetY = SWIM_BODY_Y + bob;
      const dy = targetY - this.body.position.y;
      this.body.velocity.y += dy * 9 * dt;
      if (this.keys.has('Space')) {
        this.body.velocity.y = Math.max(this.body.velocity.y, SWIM_UP_SPEED);
        this.jumpBufferLeft = 0;
      }
      this.body.velocity.y *= 0.9;

      const sprintWanted =
        !this.knocked &&
        !this.carryingSquadmate &&
        (this.keys.has('ShiftLeft') || this.keys.has('ShiftRight'));
      const move = new THREE.Vector3();
      if (this.autoRun || this.keys.has('KeyW') || this.gamepadMoveY < -0.08) move.add(forward);
      if (this.keys.has('KeyS') || this.gamepadMoveY > 0.08) move.sub(forward);
      if (this.keys.has('KeyD') || this.gamepadMoveX > 0.08) move.add(right);
      if (this.keys.has('KeyA') || this.gamepadMoveX < -0.08) move.sub(right);
      const horizMove = move.lengthSq() > 0;
      const swimSprintHeld = sprintWanted && horizMove && this.stamina > 0.5;
      this.swimSprinting = swimSprintHeld;
      let speed = swimSprintHeld ? SWIM_SPEED * SWIM_SPRINT_MULT : SWIM_SPEED;
      if (horizMove) move.normalize().multiplyScalar(speed);
      if (swimSprintHeld) {
        this.stamina = Math.max(0, this.stamina - dt * SWIM_SPRINT_STAMINA_DRAIN);
      } else {
        this.stamina = Math.min(100, this.stamina + dt * 10);
      }
      const blend = 1 - Math.exp(-7 * dt);
      this.body.velocity.x += (move.x - this.body.velocity.x) * blend;
      this.body.velocity.z += (move.z - this.body.velocity.z) * blend;
      if (!horizMove) {
        const drag = Math.exp(-5 * dt);
        this.body.velocity.x *= drag;
        this.body.velocity.z *= drag;
      }

      this.sprintHold = Math.max(0, this.sprintHold - dt * 2.2);
      this.wasSprintingForFov = false;
      this.wasTacticalSprintingForFov = false;
      this.prevGrounded = false;
      this.viewKickY *= Math.exp(-13 * dt);
      this.decayRecoil(dt);
      this.decayChestOpenPitch(dt);
      this.pitchObject.rotation.x = this.getPitchWithJuice();
      if (this.pointerLocked) {
        const strafe = (this.keys.has('KeyD') ? 1 : 0) - (this.keys.has('KeyA') ? 1 : 0);
        const target = strafe * (horizMove ? -0.028 : 0);
        this.cameraRoll += (target - this.cameraRoll) * Math.min(1, 10 * dt);
        this.camera.rotation.z = this.cameraRoll + this.juiceCameraRoll;
      } else {
        this.cameraRoll = 0;
        this.camera.rotation.z = this.juiceCameraRoll;
      }
      this.decayCameraShake(dt);
      return;
    }

    this.swimSprinting = false;

    if (this.vaulting) {
      this.vaultT += dt / VAULT_DURATION;
      const t = Math.min(1, this.vaultT);
      this.body.position.y = this.vaultStartY + (this.vaultTargetY - this.vaultStartY) * (1 - (1 - t) ** 2.4);
      const fwdSpeed = 10.8 + t * 2.4;
      this.body.velocity.x = this.vaultFwdX * fwdSpeed;
      this.body.velocity.z = this.vaultFwdZ * fwdSpeed;
      this.body.velocity.y = t < 0.58 ? 6.2 * (1 - t * 0.35) : -1.8 * (1 - t);
      if (t >= 1) {
        this.vaulting = false;
        this.body.position.y = this.vaultTargetY;
        this.body.velocity.y = 0;
      }
      this.prevGrounded = true;
      this.viewKickY *= Math.exp(-13 * dt);
      this.decayRecoil(dt);
      this.decayChestOpenPitch(dt);
      this.pitchObject.rotation.x = this.getPitchWithJuice();
      if (this.pointerLocked) {
        this.camera.rotation.z = this.cameraRoll + this.juiceCameraRoll;
      } else {
        this.camera.rotation.z = this.juiceCameraRoll;
      }
      return;
    }

    const sprintWanted =
      !this.knocked &&
      !this.carryingSquadmate &&
      (this.keys.has('ShiftLeft') || this.keys.has('ShiftRight'));
    const sprintHeld = sprintWanted && this.stamina > 0.5;
    let speed = this.knocked ? 2.65 : this.carryingSquadmate ? 4.8 : sprintHeld ? 11.4 : 7.2;
    if (!this.knocked && !this.carryingSquadmate && sprintHeld && this.sprintHold > 0.58) speed = 13.6;
    if (crouching) speed *= this.knocked ? 1 : 0.62;
    const move = new THREE.Vector3();
    const forwardHeld = this.autoRun || this.keys.has('KeyW') || this.gamepadMoveY < -0.08;
    if (forwardHeld) move.add(forward);
    if (this.keys.has('KeyS') || this.gamepadMoveY > 0.08) move.sub(forward);
    if (this.keys.has('KeyD') || this.gamepadMoveX > 0.08) move.add(right);
    if (this.keys.has('KeyA') || this.gamepadMoveX < -0.08) move.sub(right);
    const horizMove = move.lengthSq() > 0;
    if (horizMove) move.normalize();

    const slideWanted =
      !this.knocked &&
      !this.carryingSquadmate &&
      grounded &&
      !crouching &&
      (this.keys.has('ControlLeft') || this.keys.has('ControlRight')) &&
      horizMove &&
      !sprintWanted;

    if (slideWanted) {
      if (!this.sliding) {
        this.slideTime = 0;
        this.body.velocity.x *= 1.35;
        this.body.velocity.z *= 1.35;
      }
      this.sliding = true;
      this.slideTime += dt;
      const decay = Math.max(0.4, 1.4 - this.slideTime * 0.7);
      speed = 14.4 * decay;

      const tiltSide = this.keys.has('KeyA') ? 0.05 : this.keys.has('KeyD') ? -0.05 : -0.03;
      this.juiceCameraRoll += (tiltSide - this.juiceCameraRoll) * Math.min(1, 12 * dt);
    } else {
      if (this.sliding && crouching && grounded) {
        this.body.velocity.x *= SLIDE_CROUCH_MOMENTUM;
        this.body.velocity.z *= SLIDE_CROUCH_MOMENTUM;
      }
      this.sliding = false;
      this.slideTime = 0;
      this.juiceCameraRoll += (0 - this.juiceCameraRoll) * Math.min(1, 10 * dt);
    }

    if (horizMove) move.multiplyScalar(speed);

    const movingForward = forwardHeld && forward.dot(move.clone().normalize()) > 0.35;
    if (
      !this.knocked &&
      !crouching &&
      grounded &&
      movingForward &&
      this.vaultCooldown <= 0 &&
      horizMove
    ) {
      const fx = forward.x;
      const fz = forward.z;
      const chestFrom = new CANNON.Vec3(
        this.body.position.x,
        feetY + 1.05,
        this.body.position.z,
      );
      const chestTo = new CANNON.Vec3(
        this.body.position.x + fx * 0.62,
        feetY + 1.05,
        this.body.position.z + fz * 0.62,
      );
      const chestRes = new CANNON.RaycastResult();
      const chestHit =
        this.world.raycastClosest(chestFrom, chestTo, { skipBackfaces: true }, chestRes) &&
        chestRes.hasHit &&
        chestRes.distance < 0.58;
      const sprintVault = sprintHeld && this.sprintHold > 0.08 && chestHit;
      const manualVault = this.jumpBufferLeft > 0;
      if (sprintVault || manualVault) {
        const ledge = this.detectVaultLedge(feetY, fx, fz, chestHit);
        if (ledge) {
          this.startVault(ledge.topBodyY, fx, fz);
        } else {
          this.triggerMantleFailFeedback();
          if (manualVault) this.jumpBufferLeft = 0;
        }
      }
    }

    if (this.vaulting) {
      this.vaultT += dt / VAULT_DURATION;
      const t = Math.min(1, this.vaultT);
      this.body.position.y = this.vaultStartY + (this.vaultTargetY - this.vaultStartY) * (1 - (1 - t) ** 2.4);
      const fwdSpeed = 10.8 + t * 2.4;
      this.body.velocity.x = this.vaultFwdX * fwdSpeed;
      this.body.velocity.z = this.vaultFwdZ * fwdSpeed;
      this.body.velocity.y = t < 0.58 ? 6.2 * (1 - t * 0.35) : -1.8 * (1 - t);
      if (t >= 1) {
        this.vaulting = false;
        this.body.position.y = this.vaultTargetY;
        this.body.velocity.y = 0;
      }
      this.prevGrounded = true;
      this.viewKickY *= Math.exp(-13 * dt);
      this.decayRecoil(dt);
      this.decayChestOpenPitch(dt);
      this.pitchObject.rotation.x = this.getPitchWithJuice();
      if (this.pointerLocked) {
        this.camera.rotation.z = this.cameraRoll + this.juiceCameraRoll;
      } else {
        this.camera.rotation.z = this.juiceCameraRoll;
      }
      this.decayCameraShake(dt);
      return;
    }

    const vy = this.body.velocity.y;
    const gliderWanted =
      !this.knocked &&
      !this.introFrozen &&
      !grounded &&
      this.keys.has('Space') &&
      this.body.position.y > 4.2;
    const accel = grounded ? (this.sliding ? 11 : horizMove ? 20 : 16) : gliderWanted ? 7.5 : 5.2;
    const blend = 1 - Math.exp(-accel * dt);
    this.body.velocity.x += (move.x - this.body.velocity.x) * blend;
    this.body.velocity.z += (move.z - this.body.velocity.z) * blend;
    if (grounded && !horizMove) {
      const drag = Math.exp(-8 * dt);
      this.body.velocity.x *= drag;
      this.body.velocity.z *= drag;
    }
    this.body.velocity.y = vy;

    if (
      !this.knocked &&
      this.jumpBufferLeft > 0 &&
      (grounded || this.coyoteLeft > 0) &&
      this.body.velocity.y < 2.2
    ) {
      this.body.velocity.y = crouching ? 7.2 : 8.35;
      this.coyoteLeft = 0;
      this.jumpBufferLeft = 0;
    }

    const sprinting =
      (this.keys.has('ShiftLeft') || this.keys.has('ShiftRight')) && horizMove && grounded && this.stamina > 0.5;
    if (sprinting) {
      this.stamina = Math.max(0, this.stamina - dt * 20.0);
      this.sprintHold += dt;
    } else {
      this.stamina = Math.min(100.0, this.stamina + dt * 14.0);
      this.sprintHold = Math.max(0, this.sprintHold - dt * 2.2);
    }
    const tacticalSprinting = sprinting && this.sprintHold > 0.58;
    this.wasSprintingForFov = sprinting;
    this.wasTacticalSprintingForFov = tacticalSprinting;

    if (gliderWanted) {
      if (this.body.velocity.y < -4.1) this.body.velocity.y = -4.1;
      this.body.velocity.x *= 0.994;
      this.body.velocity.z *= 0.994;
    }

    if (gliderWanted && !this.prevGliderWanted) {
      this.gliderFallGraceTimer = GLIDER_FALL_GRACE_SEC;
    }
    this.prevGliderWanted = gliderWanted;
    this.gliderFallGraceTimer = Math.max(0, this.gliderFallGraceTimer - dt);
    this.gliderActive = gliderWanted;

    if (grounded && !this.prevGrounded && vyIn < -3.1) {
      this.viewKickY = Math.min(0.12, (-vyIn - 3.1) * 0.013);
      this.landingPulse = true;
    }
    this.prevGrounded = grounded;
    this.viewKickY *= Math.exp(-13 * dt);

    this.decayCameraShake(dt);

    this.decayRecoil(dt);
    if (grounded) this.updateFootstepSurface();
    this.decayChestOpenPitch(dt);
    this.pitchObject.rotation.x = this.getPitchWithJuice();

    if (this.pointerLocked) {
      const strafe = (this.keys.has('KeyD') ? 1 : 0) - (this.keys.has('KeyA') ? 1 : 0);
      const moving = move.lengthSq() > 0.5;
      const target = strafe * (moving ? -0.052 : 0);
      this.cameraRoll += (target - this.cameraRoll) * Math.min(1, 12 * dt);
      this.camera.rotation.z = this.cameraRoll + this.juiceCameraRoll;
    } else {
      this.cameraRoll = 0;
      this.camera.rotation.z = this.juiceCameraRoll;
    }

    // Dynamic FOV shifting — sprint +3° with smooth lerp
    // Phase 33X-14/15: stronger slide FOV; tiny FOV ease after mantle success
    let targetFov = this.baseFov;
    if (this.sliding) {
      const slideSpeed = Math.hypot(this.body.velocity.x, this.body.velocity.z);
      targetFov = this.baseFov + 12 + Math.min(4, slideSpeed * 0.15);
    } else if (this.getTacticalSprintActive()) {
      targetFov = this.baseFov + 8;
    } else if (sprinting && grounded) {
      targetFov = this.baseFov + 3;
    } else if (!grounded && vy < -4) {
      targetFov = this.baseFov + 4;
    }
    if (this.mantleFailCooldown <= 0 && this.vaulting) {
      targetFov += 2.5;
    }

    this.sprintFovPunch *= Math.exp(-16 * dt);
    const fovLerp = this.reducedMotion ? 14 : 10;
    this.currentFov += (targetFov + this.sprintFovPunch - this.currentFov) * Math.min(1, fovLerp * dt);
    if (Math.abs(this.currentFov - this.camera.fov) > 0.1) {
      this.camera.fov = this.currentFov;
      this.camera.updateProjectionMatrix();
    }

    if (this.body.position.y < -35) {
      this.body.velocity.set(0, 0, 0);
      this.body.position.set(0, HALF_H + 2, 18);
    }

    this.wasSliding = this.sliding;
  }
}
